import { useEffect, useState, type ReactNode } from 'react';
import { Button, type ButtonProps } from '@mui/material';
import { useTranslation } from 'react-i18next';

const REVERT_MS = 5000;

interface ConfirmButtonProps extends Omit<ButtonProps, 'onClick' | 'children'> {
  children: ReactNode;
  /** Fired on the second click within 5 s of the first. */
  onConfirm: () => void;
}

/**
 * A two-click inline control (TMMAPP-0005): the first click arms it — the label becomes
 * `common.confirm` — and a second click within 5 s fires `onConfirm`; otherwise it reverts.
 *
 * Only for **reversible, low-stakes** actions (TMM-B4, TMMAPP-0071) — promote, unlink, reverse,
 * clear, start / end a meeting. An irreversible action (delete, discard, revoke, leave, void,
 * archive, cancel a series or subscription) confirms through `ConfirmDialog` instead, which
 * names the object and states the consequence.
 */
export function ConfirmButton({ children, onConfirm, ...buttonProps }: ConfirmButtonProps) {
  const { t } = useTranslation();
  const [armed, setArmed] = useState(false);

  // The revert timer is an external system: one timeout per arming, cleared on disarm / unmount.
  useEffect(() => {
    if (!armed) return;
    const timer = setTimeout(() => setArmed(false), REVERT_MS);
    return () => clearTimeout(timer);
  }, [armed]);

  function handleClick() {
    if (armed) {
      setArmed(false);
      onConfirm();
    } else {
      setArmed(true);
    }
  }

  return (
    <Button {...buttonProps} onClick={handleClick}>
      {armed ? t('common.confirm') : children}
    </Button>
  );
}
